import { IResource } from "@gettactic/api";
import {
  clashReasonLabel,
  RecurringClashChoices,
  RecurringOccurrences
} from "@gettactic/helpers/src/reserve/recurringClashes";
import { format, parseISO } from "date-fns";
import React from "react";

const formatDay = (date: string) => format(parseISO(date), "EEE, MMM d");

/** The dates a recurring reservation can't be booked on, each to skip or to book on another resource */
export const ChatBotRecurringClashes: React.FC<{
  occurrences: RecurringOccurrences;
  resourceName: string;
  resourcesById: Record<string, IResource>;
  choices: RecurringClashChoices;
  onChange: (date: string, resourceId: string | null) => void;
  // Dates already booked on another resource; their choice is locked
  bookedDates?: string[];
  disabled?: boolean;
}> = ({
  occurrences,
  resourceName,
  resourcesById,
  choices,
  onChange,
  bookedDates = [],
  disabled
}) => {
  // Unique per card, as several reservations can clash on the same date
  const idPrefix = React.useId();
  const { dates, clashes } = occurrences;
  if (clashes.length === 0) return null;

  return (
    <section
      aria-label="Dates that need a choice"
      className="mt-4 flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm"
    >
      <p className="font-medium text-neutral-900">
        {clashes.length} of {dates.length} dates can't be booked
      </p>
      {occurrences.has_more && (
        <p className="text-neutral-600">
          This repeats past the {dates.length} dates checked, so booking covers
          them, through {formatDay(dates[dates.length - 1])}.
        </p>
      )}
      <ul className="flex flex-col gap-2">
        {clashes.map((clash) => {
          const id = `${idPrefix}-recurring-clash-${clash.date}`;
          return (
            <li
              key={clash.date}
              className="flex flex-wrap items-center justify-between gap-2"
            >
              <label htmlFor={id} className="min-w-0">
                <span className="font-medium text-neutral-900">
                  {formatDay(clash.date)}
                </span>
                <span className="text-neutral-600">
                  {" "}
                  · {clashReasonLabel(clash.reason, resourceName)}
                </span>
                {bookedDates.includes(clash.date) && (
                  <span className="text-neutral-600"> · Booked</span>
                )}
              </label>
              <select
                id={id}
                className="h-8 rounded-md border border-neutral-300 bg-white px-2 text-neutral-900 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600"
                value={choices[clash.date] ?? ""}
                onChange={(event) =>
                  onChange(clash.date, event.target.value || null)
                }
                disabled={
                  disabled ||
                  clash.alternative_resource_ids.length === 0 ||
                  bookedDates.includes(clash.date)
                }
              >
                <option value="">Skip this date</option>
                {clash.alternative_resource_ids.map((resourceId) => (
                  <option key={resourceId} value={resourceId}>
                    Book {resourcesById[resourceId]?.name ?? "another one"}{" "}
                    instead
                  </option>
                ))}
              </select>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
